/**
 * The aggregated picture of the household's finances, computed from the Sheet.
 * Pure: the dashboard renders it and the recommendation step sends it to
 * Claude in place of raw documents. See ARCHITECTURE.md, Pipeline step 6.
 */
import { catalog } from './categories'
import { applyCorrections, isOneOff, keyCorrections } from './corrections'
import { dedupeTransactions, oneHoldingsSource, withTxnKeys } from './duplicates'
import { accountKey, slug } from './filing'
import { formatDate, isIsoDate, monthsOfData } from './dates'
import type { ACCOUNT_KINDS } from './extraction'
import type { Row } from './google/sheets'


export type Group = 'cash' | 'taxable' | 'retirement' | 'hsa' | 'education' | 'property' | 'debt' | 'other'

export const GROUP_LABEL: Record<Group, string> = {
  cash: 'Cash',
  taxable: 'Taxable investments',
  retirement: 'Retirement',
  hsa: 'HSA',
  education: 'Education (529)',
  property: 'Home & property',
  debt: 'Debt',
  other: 'Other',
}

/** Manual-tab categories people are likely to type, mapped to a group. */
const MANUAL_GROUP: Record<string, Group> = {
  home: 'property',
  house: 'property',
  real_estate: 'property',
  property: 'property',
  rental: 'property',
  land: 'property',
  vehicle: 'property',
  car: 'property',
  boat: 'property',
  '401k': 'retirement',
  '403b': 'retirement',
  ira: 'retirement',
  roth_ira: 'retirement',
  retirement: 'retirement',
  pension: 'retirement',
  brokerage: 'taxable',
  investment: 'taxable',
  investments: 'taxable',
  crypto: 'taxable',
  stock: 'taxable',
  cash: 'cash',
  checking: 'cash',
  savings: 'cash',
  hsa: 'hsa',
  '529': 'education',
  education: 'education',
}

const DEBT_TYPES = new Set(['debt', 'liability', 'loan', 'owed'])

type AccountKind = (typeof ACCOUNT_KINDS)[number]

/** Every kind but 'other' must be placed, so a new kind fails to compile until it is. */
const GROUP_OF: Record<Exclude<AccountKind, 'other'>, Group> = {
  checking: 'cash',
  savings: 'cash',
  brokerage: 'taxable',
  ira: 'retirement',
  roth_ira: 'retirement',
  '401k': 'retirement',
  '529': 'education',
  hsa: 'hsa',
  credit: 'debt',
  mortgage: 'debt',
  auto_loan: 'debt',
  student_loan: 'debt',
}

/** Day-to-day money flows through these; investment accounts' trades and dividends are not household cash flow. */
const CASH_FLOW_KINDS = new Set<string>(['checking', 'savings', 'credit'] satisfies AccountKind[])

export interface AccountSummary {
  account_id: string
  institution: string
  kind: string
  group: Group
  name: string
  last4: string
  balance: number | null
  as_of: string | null
  credit_limit: number | null
  apr: number | null
  /** Share of the credit limit in use, 0–1, for cards with a known limit. */
  utilization: number | null
  /** Entered by hand in the Manual tab rather than read from a statement. */
  manual?: boolean
}

export interface Amount {
  label: string
  total: number
  count: number
}

export interface Recurring {
  merchant: string
  months: number
  typical: number
  category: string
}

export interface MonthFlow {
  month: string
  income: number
  spending: number
  net: number
}

export interface NetWorthPoint {
  /** `YYYY-MM`; the value is as of that month's last statement. */
  month: string
  assets: number
  debts: number
  net: number
  /** Value per group that month (debt as a positive amount owed), for the chart's hover. */
  by_group: Partial<Record<Group, number>>
}

/**
 * How one transaction is counted. The dashboard's figures are sums over these,
 * so a drill-down list of `spending` rows adds up to the figure clicked.
 */
export type Treatment = 'spending' | 'income' | 'one_off' | 'excluded'

export interface LedgerRow {
  txn_id: string
  /** What a correction is saved against: account, date, amount and occurrence. */
  txn_key: string
  date: string
  month: string
  account_id: string
  merchant: string
  description: string
  amount: number
  /** A category_id from the Categories tab. */
  category: string
  /** Its parent, as the charts group it: "Cars" for Cars / Fuel. */
  group: string
  /** Its full name: "Cars / Fuel", or "Groceries" for a category with no subcategory. */
  label: string
  /** The category Claude read from the statement, before corrections. */
  extracted_category: string
  /** 'you', 'rule: <match>' or ''. */
  corrected_by: string
  /** The key the correction is stored under, when it isn't txn_key (carried over from a Tiller row). */
  stored_key?: string
  treatment: Treatment
  /** Why an excluded row is excluded, in words. */
  reason: string
  /** Inside the twelve months the spending figures cover. */
  in_window: boolean
  /** On a checking, savings or card account: the accounts the spending figures count. */
  cash: boolean
}

export interface Holding {
  symbol: string
  name: string
  asset_class: string
  value: number
  /** Share of all holdings, 0–1. */
  share: number
  expense_ratio: number | null
  unrealized: number | null
  accounts: string[]
}

export interface Snapshot {
  /** Latest date seen in any balance or transaction. */
  as_of: string | null
  accounts: AccountSummary[]
  groups: (Amount & { group: Group })[]
  totals: { assets: number; debts: number; net: number; cash: number; invested: number }
  net_worth: NetWorthPoint[]
  cash_flow: {
    months: MonthFlow[]
    income: number
    spending: number
    /** (income − spending) / income over the months shown, or null with no income. */
    savings_rate: number | null
    avg_monthly_spending: number
    /** Cash balances ÷ average monthly spending. */
    emergency_fund_months: number | null
  }
  spending: {
    from: string | null
    to: string | null
    total: number
    by_month: Amount[]
    /** By parent, as the charts show it. */
    by_category: Amount[]
    /** By full name: Cars / Fuel and Cars / Maintenance apart. */
    by_subcategory: Amount[]
    top_merchants: Amount[]
    /** What each account paid for, by full category name, so the review can weigh card rewards against where the money goes. */
    by_account: { account_id: string; total: number; by_category: Amount[]; top_merchants: Amount[] }[]
    fees_and_interest: number
    recurring: Recurring[]
    largest: { date: string; merchant: string; amount: number; category: string; account_id: string }[]
    /** Purchases marked one-off: real money out, left out of the totals and averages above. */
    one_offs: { total: number; count: number; items: { date: string; merchant: string; amount: number; category: string; account_id: string }[] }
  }
  investments: {
    /** Value of the holdings listed on the latest statement of each account. */
    total: number
    allocation: (Amount & { share: number })[]
    top_holdings: Holding[]
    /** Value-weighted expense ratio, percent, over holdings whose ratio is known. */
    weighted_expense_ratio: number | null
    /** Share of holdings value with a known expense ratio, 0–1. */
    expense_ratio_coverage: number
    unrealized_gain: number | null
    /** Each account's mix, for asset-location questions (bonds in the IRA, not the taxable account). */
    by_account: { account_id: string; kind: string; value: number; mix: Record<string, number> }[]
  }
  /** Kept for the earlier dashboard; same as investments.allocation. */
  holdings_by_class: Amount[]
  documents: number
}

const str = (v: unknown) => (v === null || v === undefined ? '' : String(v))
const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : v === null || v === undefined || v === '' ? null : Number(v))
const round = (n: number) => Math.round(n * 100) / 100

function add(map: Map<string, Amount>, label: string, amount: number) {
  const a = map.get(label) ?? { label, total: 0, count: 0 }
  a.total += amount
  a.count += 1
  map.set(label, a)
}

function sorted(map: Map<string, Amount>, by: 'total' | 'label' = 'total'): Amount[] {
  const list = [...map.values()].map((a) => ({ ...a, total: round(a.total) }))
  return by === 'total' ? list.sort((a, b) => b.total - a.total) : list.sort((a, b) => a.label.localeCompare(b.label))
}

/** `YYYY-MM-DD` twelve months before `date`, so the window is the last year of data. */
function yearBefore(date: string): string {
  const [y, m, d] = date.split('-')
  return `${Number(y) - 1}-${m}-${d}`
}

function nextMonth(month: string): string {
  const [y, m] = month.split('-').map(Number)
  return m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, '0')}`
}

function groupOf(kind: string, balance: number | null): Group {
  return GROUP_OF[kind as Exclude<AccountKind, 'other'>] ?? (balance !== null && balance < 0 ? 'debt' : 'other')
}

/**
 * Latest date seen in any transaction or balance: the end of the spending
 * window. A date after `today` is a misread year, not data, so one bad row
 * cannot move the window.
 */
function latestDate(tables: { transactions: Row[]; balances: Row[] }, today: string): string | null {
  const dates = [...tables.transactions.map((t) => str(t.date)), ...tables.balances.map((b) => str(b.as_of))]
    .filter((d) => isIsoDate(d) && d <= today)
    .sort()
  return dates.at(-1) ?? null
}

/** Today in local time, as the overdue check and the digest see it. */
const localToday = () => formatDate(new Date())

/** Ids whose meaning is fixed by the ledger: interest charges and fees count as spending, `other` money in is not a refund. */
const SPECIAL = new Set(['interest', 'fees', 'other'])

/**
 * Every transaction, deduplicated and corrected, with how the analysis counts
 * it. The spending, income and cash-flow figures are sums over this list.
 */
export function ledger(
  tables: { accounts: Row[]; balances: Row[]; transactions: Row[]; corrections?: Row[]; rules?: Row[]; categories?: Row[] },
  today = localToday(),
): LedgerRow[] {
  const cats = catalog(tables.categories)
  const kindOf = new Map(tables.accounts.map((a) => [str(a.account_id), str(a.kind)]))
  const keyed = withTxnKeys(tables.transactions)
  const corrected = applyCorrections(dedupeTransactions(keyed), keyCorrections(tables.corrections, keyed), tables.rules)
  const asOf = latestDate(tables, today)
  const from = asOf ? yearBefore(asOf) : null
  return corrected.flatMap((t) => {
    const amount = num(t.amount)
    if (amount === null) return []
    const category = str(t.category) || 'other'
    // interest, fees and other are handled by id below, whatever type the tab gives them.
    const type = SPECIAL.has(category) ? 'spending' : cats.type(category)
    const date = str(t.date)
    const cashAccount = CASH_FLOW_KINDS.has(kindOf.get(str(t.account_id)) ?? '')
    let treatment: Treatment = 'spending'
    let reason = ''
    if (!cashAccount) {
      treatment = 'excluded'
      reason = 'investment or loan account activity'
    } else if (amount > 0 && (type === 'income' || category === 'interest')) {
      treatment = 'income'
    } else if (type !== 'spending') {
      // Money moving between the household's own accounts, or income going out, is not spending.
      treatment = 'excluded'
      reason = type === 'transfer' ? 'transfer between your own accounts' : `${cats.label(category).toLowerCase()}, not spending`
    } else if (amount === 0 || (amount > 0 && category === 'other')) {
      // Money in with no spending category is a payment received or a deposit, not a refund.
      treatment = 'excluded'
      reason = 'money in that is not income or a refund: a payment received or a deposit'
    } else if (isOneOff(t)) {
      if (amount < 0) treatment = 'one_off'
      else {
        treatment = 'excluded'
        reason = 'money in on a one-off, such as a sale: not counted'
      }
    }
    // Money in with a spending category (shopping, dining…) is a refund: it stays
    // 'spending' with a positive amount, so it nets off what was spent.
    return [
      {
        txn_id: str(t.txn_id),
        txn_key: str(t.txn_key),
        date,
        month: date.slice(0, 7),
        account_id: str(t.account_id),
        merchant: str(t.merchant) || str(t.description),
        description: str(t.description),
        amount,
        category,
        group: cats.group(category),
        label: cats.label(category),
        extracted_category: str(t.extracted_category) || category,
        corrected_by: str(t.corrected_by),
        ...(str(t.stored_key) ? { stored_key: str(t.stored_key) } : {}),
        treatment,
        reason,
        in_window: cashAccount && !!from && date > from && date <= asOf!,
        cash: cashAccount,
      },
    ]
  })
}

export function analyse(tables: {
  accounts: Row[]
  balances: Row[]
  transactions: Row[]
  holdings: Row[]
  documents: Row[]
  /** The Manual tab: a home, a car loan, a 401(k) with no statements uploaded. */
  manual?: Row[]
  /** The Corrections and Rules tabs: how the person said to count transactions. */
  corrections?: Row[]
  rules?: Row[]
  /** The Categories tab: names, parents and how each is counted. */
  categories?: Row[]
}, today = localToday()): Snapshot {
  const rows = ledger(tables, today)
  const cats = catalog(tables.categories)
  // Fees and interest, and any subcategory added under them (Fees / ATM).
  const feeGroups = new Set(['fees', 'interest'].filter((id) => !cats.list.find((c) => c.id === id)?.subcategory).map((id) => cats.group(id)))
  const isFee = (t: LedgerRow) => t.category === 'fees' || t.category === 'interest' || feeGroups.has(t.group)
  // A row with no account (a document Claude could not place) or a date that
  // is not YYYY-MM-DD (a hand edit) cannot be placed in time or in a total.
  const placed = (r: Row, date: unknown) => !!str(r.account_id) && isIsoDate(str(date))
  tables = {
    ...tables,
    balances: tables.balances.filter((b) => placed(b, b.as_of)),
    holdings: oneHoldingsSource(tables.holdings.filter((h) => placed(h, h.as_of))),
  }
  const kindOf = new Map(tables.accounts.map((a) => [str(a.account_id), str(a.kind)]))

  // --- Balances: latest per account, and a month-by-month series ---------------
  const history = new Map<string, Row[]>()
  for (const b of tables.balances) {
    if (num(b.balance) === null) continue
    const id = str(b.account_id)
    history.set(id, [...(history.get(id) ?? []), b])
  }
  for (const list of history.values()) list.sort((a, b) => str(a.as_of).localeCompare(str(b.as_of)))

  // A closed account (or a card replaced by a new number) no longer counts
  // towards today's totals; its history still counts up to its last statement.
  // Matched by bank and last 4, as the overdue check matches them, so closing
  // an account closes it under every spelling of its id.
  const closedKeys = new Set(tables.accounts.filter((a) => str(a.closed)).map((a) => accountKey(str(a.account_id))))
  const isClosed = (id: string) => closedKeys.has(accountKey(id))
  const accounts: AccountSummary[] = tables.accounts.map((a) => {
    const b = history.get(str(a.account_id))?.at(-1)
    const balance = isClosed(str(a.account_id)) ? null : num(b?.balance)
    const limit = num(b?.credit_limit)
    return {
      account_id: str(a.account_id),
      institution: str(a.institution),
      kind: str(a.kind),
      group: groupOf(str(a.kind), balance),
      name: str(a.name),
      last4: str(a.last4),
      balance,
      as_of: b ? str(b.as_of) : null,
      credit_limit: limit,
      apr: num(b?.apr) ?? num([...(history.get(str(a.account_id)) ?? [])].reverse().find((r) => num(r.apr) !== null)?.apr),
      utilization: balance !== null && limit ? round(Math.max(0, -balance) / limit) : null,
    }
  })

  // Manual entries count at their stated value; a debt is stored as a positive
  // amount owed (or a negative number) and becomes a negative balance.
  const manual: AccountSummary[] = (tables.manual ?? []).flatMap((m) => {
    const value = num(m.value)
    if (!str(m.name) || value === null) return []
    const category = slug(str(m.category)).replace(/-/g, '_')
    const debt = DEBT_TYPES.has(str(m.type).trim().toLowerCase()) || value < 0
    return [
      {
        account_id: `manual-${slug(str(m.name))}`,
        institution: '',
        kind: category || (debt ? 'debt' : 'asset'),
        group: debt ? 'debt' : (MANUAL_GROUP[category] ?? 'other'),
        name: str(m.name),
        last4: '',
        balance: debt ? -Math.abs(value) : value,
        as_of: str(m.as_of) || null,
        credit_limit: null,
        apr: num(m.rate),
        utilization: null,
        manual: true,
      },
    ]
  })
  accounts.push(...manual)
  const manualAssets = manual.reduce((s, m) => s + Math.max(0, m.balance!), 0)
  const manualDebts = manual.reduce((s, m) => s + Math.max(0, -m.balance!), 0)

  const groups = new Map<string, Amount>()
  let assets = 0
  let debts = 0
  for (const a of accounts) {
    if (a.balance === null) continue
    add(groups, a.group, a.group === 'debt' ? -a.balance : a.balance)
    if (a.group === 'debt' || a.balance < 0) debts += -a.balance
    else assets += a.balance
  }
  const groupTotal = (g: Group) => groups.get(g)?.total ?? 0

  // A month is plotted once every account that matters has reported by its
  // end, so the line doesn't jump when a large account's statements start
  // later than the rest. An account holding under 5% of the total (a new
  // card, a small savings account) doesn't hold the start back; it counts from
  // its first statement. Manual entries have one value, so they count the
  // same in every month.
  const netWorth: NetWorthPoint[] = []
  const weight = (l: Row[]) => Math.abs(num(l.at(-1)!.balance) ?? 0)
  const scale = [...history.values()].reduce((s, l) => s + weight(l), 0)
  const firstSeen = [...history.values()]
    .filter((l) => weight(l) >= scale * 0.05)
    .map((l) => str(l[0].as_of).slice(0, 7))
    .sort()
  const lastSeen = [...history.values()].map((l) => str(l.at(-1)!.as_of).slice(0, 7)).sort()
  if (firstSeen.length) {
    for (let month = firstSeen.at(-1)!; month <= lastSeen.at(-1)!; month = nextMonth(month)) {
      const end = `${month}-31`
      let a = manualAssets
      let d = manualDebts
      const byGroup: Partial<Record<Group, number>> = {}
      const put = (g: Group, v: number) => (byGroup[g] = round((byGroup[g] ?? 0) + v))
      for (const m of manual) put(m.group, m.group === 'debt' ? -m.balance! : m.balance!)
      for (const [id, list] of history) {
        if (isClosed(id) && month > str(list.at(-1)!.as_of).slice(0, 7)) continue
        const b = [...list].reverse().find((r) => str(r.as_of) <= end)
        const v = num(b?.balance)
        if (v === null) continue
        const g = groupOf(kindOf.get(id) ?? '', v)
        if (g === 'debt' || v < 0) d += -v
        else a += v
        put(g, g === 'debt' ? -v : v)
      }
      netWorth.push({ month, assets: round(a), debts: round(d), net: round(a - d), by_group: byGroup })
    }
  }

  // --- Transactions: spending and cash flow over the last year ---------------
  const asOf = latestDate(tables, today)
  const window = rows.filter((t) => t.in_window)

  const byMonth = new Map<string, Amount>()
  const byCategory = new Map<string, Amount>()
  const bySubcategory = new Map<string, Amount>()
  const byMerchant = new Map<string, Amount>()
  const spentBy = new Map<string, { total: number; categories: Map<string, Amount>; merchants: Map<string, Amount> }>()
  const flows = new Map<string, MonthFlow>()
  const merchantMonths = new Map<string, { months: Set<string>; amounts: number[]; category: string }>()
  const largest: Snapshot['spending']['largest'] = []
  let total = 0
  let income = 0
  let feesAndInterest = 0
  let first: string | null = null
  const oneOffs: Snapshot['spending']['one_offs'] = { total: 0, count: 0, items: [] }

  for (const t of window) {
    if (t.treatment === 'excluded') continue
    const { amount, category, date, month } = t
    const flow = flows.get(month) ?? { month, income: 0, spending: 0, net: 0 }
    flows.set(month, flow)
    if (t.treatment === 'income') {
      flow.income += amount
      income += amount
      continue
    }
    const spent = -amount
    const merchant = t.merchant
    if (t.treatment === 'one_off') {
      oneOffs.total += spent
      oneOffs.count += 1
      oneOffs.items.push({ date, merchant, amount: round(spent), category, account_id: t.account_id })
      continue
    }
    if (!first || date < first) first = date
    total += spent
    flow.spending += spent
    if (isFee(t)) feesAndInterest += spent
    add(byMonth, month, spent)
    add(byCategory, t.group, spent)
    add(bySubcategory, t.label, spent)
    add(byMerchant, merchant, spent)
    const acct = spentBy.get(t.account_id) ?? { total: 0, categories: new Map(), merchants: new Map() }
    acct.total += spent
    // By full name, so the card review sees Cars / Fuel apart from the rest of Cars.
    add(acct.categories, t.label, spent)
    add(acct.merchants, merchant, spent)
    spentBy.set(t.account_id, acct)
    // A refund nets off the totals above, but isn't a purchase or a bill.
    if (spent <= 0) continue
    largest.push({ date, merchant, amount: round(spent), category, account_id: str(t.account_id) })
    const m = merchantMonths.get(merchant) ?? { months: new Set(), amounts: [], category }
    m.months.add(month)
    m.amounts.push(spent)
    merchantMonths.set(merchant, m)
  }

  // A charge seen in 2+ months at a steady amount (within 10% of its median)
  // is treated as a subscription or bill.
  const recurring: Recurring[] = []
  for (const [merchant, m] of merchantMonths) {
    if (m.months.size < 2) continue
    const amounts = [...m.amounts].sort((a, b) => a - b)
    const median = amounts[Math.floor(amounts.length / 2)]
    const steady = amounts.filter((a) => Math.abs(a - median) <= median * 0.1).length
    if (steady >= 2 && steady >= m.months.size) {
      recurring.push({ merchant, months: m.months.size, typical: round(median), category: m.category })
    }
  }
  recurring.sort((a, b) => b.typical - a.typical)

  const months = [...flows.values()]
    .sort((a, b) => a.month.localeCompare(b.month))
    .map((f) => ({ month: f.month, income: round(f.income), spending: round(f.spending), net: round(f.income - f.spending) }))
  // Divided by the months of data, not the calendar months touched: twelve
  // months to the day touch 13, and six months starting mid-month touch 7.
  const dataFrom = window.reduce<string | null>((min, t) => (!min || t.date < min ? t.date : min), null)
  const avgSpending = months.length && dataFrom && asOf ? total / Math.min(12, monthsOfData(dataFrom, asOf)) : 0
  const cash = groupTotal('cash')

  // --- Holdings: the latest statement of each account ---------------------
  const latestHolding = new Map<string, string>()
  for (const h of tables.holdings) {
    const id = str(h.account_id)
    if (str(h.as_of) > (latestHolding.get(id) ?? '')) latestHolding.set(id, str(h.as_of))
  }
  const current = tables.holdings.filter(
    (h) => str(h.as_of) === latestHolding.get(str(h.account_id)) && num(h.value) !== null && !isClosed(str(h.account_id)),
  )
  const invested = current.reduce((s, h) => s + num(h.value)!, 0)

  const byClass = new Map<string, Amount>()
  // Gain is counted only over the holdings whose cost basis is printed: an IRA
  // that prints none must not wipe out the taxable account's gain.
  const bySymbol = new Map<string, Holding & { basis: number; basisValue: number; hasBasis: boolean }>()
  let erValue = 0
  let erWeighted = 0
  for (const h of current) {
    const value = num(h.value)!
    add(byClass, str(h.asset_class) || 'other', value)
    const er = num(h.expense_ratio)
    if (er !== null) {
      erValue += value
      erWeighted += value * er
    }
    const key = str(h.symbol) || str(h.name)
    const held = bySymbol.get(key) ?? {
      symbol: str(h.symbol),
      name: str(h.name),
      asset_class: str(h.asset_class) || 'other',
      value: 0,
      share: 0,
      expense_ratio: er,
      unrealized: null,
      basis: 0,
      basisValue: 0,
      hasBasis: false,
      accounts: [],
    }
    held.value += value
    held.expense_ratio ??= er
    const basis = num(h.cost_basis)
    if (basis !== null) {
      held.basis += basis
      held.basisValue += value
      held.hasBasis = true
    }
    if (!held.accounts.includes(str(h.account_id))) held.accounts.push(str(h.account_id))
    bySymbol.set(key, held)
  }
  let unrealized: number | null = null
  const holdings: Holding[] = [...bySymbol.values()]
    .map(({ basis, basisValue, hasBasis, ...h }) => {
      const gain = hasBasis ? round(basisValue - basis) : null
      if (gain !== null) unrealized = (unrealized ?? 0) + gain
      return { ...h, value: round(h.value), share: invested ? round(h.value / invested) : 0, unrealized: gain }
    })
    .sort((a, b) => b.value - a.value)
  const perAccount = new Map<string, Map<string, number>>()
  for (const h of current) {
    const id = str(h.account_id)
    const mix = perAccount.get(id) ?? new Map<string, number>()
    const cls = str(h.asset_class) || 'other'
    mix.set(cls, (mix.get(cls) ?? 0) + num(h.value)!)
    perAccount.set(id, mix)
  }
  const byAccount = [...perAccount].map(([account_id, mix]) => {
    const value = [...mix.values()].reduce((s, v) => s + v, 0)
    return {
      account_id,
      kind: kindOf.get(account_id) ?? '',
      value: round(value),
      mix: Object.fromEntries([...mix].map(([k, v]) => [k, value ? Math.round((v / value) * 1000) / 1000 : 0])),
    }
  })
  const allocation = sorted(byClass).map((a) => ({ ...a, share: invested ? Math.round((a.total / invested) * 1000) / 1000 : 0 }))

  return {
    as_of: asOf,
    accounts,
    groups: sorted(groups).map((g) => ({ ...g, group: g.label as Group })),
    totals: {
      assets: round(assets),
      debts: round(debts),
      net: round(assets - debts),
      cash: round(cash),
      invested: round(groupTotal('taxable') + groupTotal('retirement') + groupTotal('hsa') + groupTotal('education')),
    },
    net_worth: netWorth,
    cash_flow: {
      months,
      income: round(income),
      spending: round(total),
      savings_rate: income > 0 ? Math.round(((income - total) / income) * 1000) / 1000 : null,
      avg_monthly_spending: round(avgSpending),
      emergency_fund_months: avgSpending > 0 ? Math.round((cash / avgSpending) * 10) / 10 : null,
    },
    spending: {
      from: first,
      to: asOf,
      total: round(total),
      by_month: sorted(byMonth, 'label'),
      by_category: sorted(byCategory),
      by_subcategory: sorted(bySubcategory),
      top_merchants: sorted(byMerchant).slice(0, 10),
      by_account: [...spentBy]
        .map(([account_id, a]) => ({ account_id, total: round(a.total), by_category: sorted(a.categories), top_merchants: sorted(a.merchants).slice(0, 10) }))
        .sort((a, b) => b.total - a.total),
      fees_and_interest: round(feesAndInterest),
      recurring,
      largest: largest.sort((a, b) => b.amount - a.amount).slice(0, 10),
      one_offs: { ...oneOffs, total: round(oneOffs.total), items: oneOffs.items.sort((a, b) => b.amount - a.amount).slice(0, 10) },
    },
    investments: {
      total: round(invested),
      allocation,
      top_holdings: holdings.slice(0, 15),
      weighted_expense_ratio: erValue ? Math.round((erWeighted / erValue) * 1000) / 1000 : null,
      expense_ratio_coverage: invested ? Math.round((erValue / invested) * 1000) / 1000 : 0,
      unrealized_gain: unrealized === null ? null : round(unrealized),
      by_account: byAccount.sort((a, b) => b.value - a.value),
    },
    holdings_by_class: allocation,
    documents: tables.documents.filter((d) => d.status === 'done').length,
  }
}
